import React from "react";
import { ShieldCheck, Lock, UserCheck, ClipboardCheck, Phone, Mail, MapPin, User } from "lucide-react";
import { hipaaContent, images, hipaaPolicySections, hipaaPrivacyOfficer } from "../mock/mock";
import { PageHero, SectionHeading, CTABanner } from "../components/Common";

const iconMap = [ShieldCheck, Lock, UserCheck, ClipboardCheck];

const ContactCard = () => (
  <div className="my-4 rounded-xl border border-border bg-secondary p-6">
    <div className="section-eyebrow text-gold-dark mb-3">Privacy Officer — Contact Information</div>
    <div className="space-y-2.5 text-sm">
      <div className="flex items-center gap-3 text-navy font-semibold">
        <User size={16} className="text-gold-dark shrink-0" /> {hipaaPrivacyOfficer.name}
      </div>
      <div className="flex items-center gap-3 text-muted-foreground">
        <Phone size={16} className="text-gold-dark shrink-0" />
        <a href={`tel:${hipaaPrivacyOfficer.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-navy transition-colors">
          {hipaaPrivacyOfficer.phone}
        </a>
      </div>
      <div className="flex items-center gap-3 text-muted-foreground">
        <Mail size={16} className="text-gold-dark shrink-0" />
        <a href={`mailto:${hipaaPrivacyOfficer.email}`} className="hover:text-navy transition-colors">
          {hipaaPrivacyOfficer.email}
        </a>
      </div>
      <div className="flex items-start gap-3 text-muted-foreground">
        <MapPin size={16} className="text-gold-dark shrink-0 mt-0.5" /> {hipaaPrivacyOfficer.address}
      </div>
    </div>
  </div>
);

const Block = ({ block }) => {
  if (block.p) return <p className="text-muted-foreground leading-relaxed mb-3">{block.p}</p>;
  if (block.sub) return <h4 className="font-heading font-semibold text-navy text-base mt-5 mb-2">{block.sub}</h4>;
  if (block.ul)
    return (
      <ul className="mb-3 space-y-2">
        {block.ul.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  if (block.contact) return <ContactCard />;
  return null;
};

const Hipaa = () => {
  return (
    <div>
      <PageHero
        eyebrow="Compliance"
        title={hipaaContent.title}
        subtitle={hipaaContent.intro}
        image={images.medReception2}
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            center
            eyebrow="How We Protect Data"
            title="Built HIPAA-first across people, process & technology"
          />
          <div className="grid sm:grid-cols-2 gap-6">
            {hipaaContent.sections.map((s, i) => {
              const Icon = iconMap[i % iconMap.length];
              return (
                <div key={s.title} className="rounded-2xl border border-border p-8 flex gap-5 card-lift">
                  <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                    <Icon size={22} className="text-gold-light" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-navy text-lg mb-2">{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-secondary py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-bold text-navy-dark mb-6">
            <ShieldCheck size={18} /> 100% HIPAA Compliant
          </div>
          <p className="text-muted-foreground leading-relaxed">
            With a medical answering service compliant for HIPAA, you can always be assured that the
            data of your patients is 100 percent safe at all times. It protects you and your practice,
            clinic and hospital from the effects of non-compliance.
          </p>
        </div>
      </section>

      {/* Full HIPAA Privacy Policy document */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Official Policy"
            title="HIPAA Privacy Policy"
            subtitle="Our full policy governing the privacy and security of Protected Health Information (PHI)."
          />
          <div className="space-y-10">
            {hipaaPolicySections.map((sec) => (
              <div key={sec.num} className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
                  <span className="font-heading font-extrabold text-gold-dark text-lg">{sec.num}</span>
                  <h3 className="font-heading font-bold text-navy text-xl">{sec.title}</h3>
                </div>
                {sec.blocks.map((block, i) => (
                  <Block key={i} block={block} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="Ready for a compliant healthcare answering partner?" />
    </div>
  );
};

export default Hipaa;
